import React, { useCallback, useEffect, useRef, useState } from "react";
import { GamesCategoriesSlider } from "./GamesCategoriesSlider";
import { useLocation, useParams } from "react-router-dom";
import { cn } from "helper/Utils";
import { isEmpty } from "helper/Functions";
import { GameCard } from "../GameCard";
import GameService from "service/GameService";
import InfiniteScroll from "react-infinite-scroller";
import { ProviderListSearch } from "./ProviderListSearch";
import { GamesFiltersBar } from "./GamesFiltersBar";
import { GAMES_CATEGORY_NAV } from "routes/APP_ROUTES";
import Drawer from "components/ui/Drawer";
import Modal from "components/ui/Modal";
import Button from "components/ui/Button";
import { SkeletonLoader } from "components/ui/loaders/SkeletonLoader";
import { debounce } from "lodash";
import { API_URL } from "helper/Constants";
import { useTranslation } from "react-i18next";

export const GamesSearchByFilters = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { category } = useParams();
  const gameService = new GameService(API_URL);

  // Add a ref to store the last search term to prevent duplicate API calls
  const lastSearchRef = useRef("");

  // FILTERS STATES
  const [isProvidersModalWarning, setIsProvidersModalWarning] = useState(false);
  const [isProvidersModal, setIsProvidersModal] = useState(false);
  const [isProvidersVisible, setIsProvidersVisible] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedProviders, setSelectedProviders] = useState(null);
  const [searchGames, setSearchGames] = useState("");
  const [searchProviders, setSearchProviders] = useState("");
  const [sortOrder, setSortOrder] = useState(false);
  // FILTERS STATES

  const [allProviders, setAllProviders] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isProviderLoading, setIsProviderLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalGames, setTotalGames] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isSearchMode, setIsSearchMode] = useState(false);

  const [matchesMedia, setMatchesMedia] = useState({
    isMobile: window?.innerWidth <= 540,
    isTablet: window?.innerWidth <= 768,
  });

  const isLoadingRef = useRef(false);
  let limit = matchesMedia.isMobile ? 16 : 35;
  const [offset, setOffset] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const lastPage = Math.ceil(totalGames / limit) - 1 || Math.ceil(totalPages / pageNumber) - 1;

  useEffect(() => {
    const handleResize = () => {
      setMatchesMedia({
        isMobile: window?.innerWidth <= 540,
        isTablet: window?.innerWidth <= 768,
      });
    };
    if (matchesMedia.isTablet) {
      setIsProvidersVisible(false);
    } else {
      setIsProvidersVisible(true);
    }

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [matchesMedia]);

  const gameCategoryNavItems = GAMES_CATEGORY_NAV();

  const isCasino = location.pathname.startsWith("/casino");
  const isLiveCasino = location.pathname.startsWith("/live-casino");

  const loadFilteredGames = useCallback(async () => {
    if (loading || (!hasMore && !selectedProviders)) return;
    isLoadingRef.current = true;

    // Reset search loading state when loading filtered games
    setSearchLoading(false);

    const newCat = !isLiveCasino
      ? gameCategoryNavItems?.CASINO?.find((fi) => fi.path === `/${category}`)
      : gameCategoryNavItems?.LIVE_CASINO?.find((fi) => fi.path === `/${category}`);

    let liveCasinoTypes = ["live-casino-table", "livecasino"];
    let typeFilter = isCasino ? newCat?.category : isLiveCasino ? liveCasinoTypes : null;
    let providerFilter =
      !isLiveCasino && newCat?.category === "" && !isEmpty(searchGames) && isEmpty(selectedProviders) && offset === 0
        ? "Pragmatic Play"
        : selectedProviders;
    try {
      setLoading(true);
      const filters = {
        ...(typeFilter && { type: typeFilter }),
        provider_name: providerFilter,
        sortOrder: sortOrder ? "desc" : "asc",
      };

      let response = [];
      response = await gameService.fetchGames(offset, limit, filters);

      const newGames = response.data || [];
      const total = response.pagination?.total || 0;

      if (offset === 0) {
        // Initial load
        setGames(newGames);
      } else {
        // Load more - append new games to existing games
        setGames((prevGames) => [...prevGames, ...newGames]);
      }

      // Calculate hasMore properly
      setHasMore(newGames.length > 0 && offset + newGames.length < total);
      setTotalGames(total);
    } catch (error) {
      console.error("Error fetching games:", error);
      setError(error);
    } finally {
      setLoading(false);
      isLoadingRef.current = false; // Reset the loading guard
    }
  }, [category, gameService, hasMore, isCasino, isLiveCasino, limit, loading, offset, searchGames, selectedProviders, sortOrder]);

  const handleSelectedType = (type) => {
    setOffset(0);
    setSelectedCategory(type);
    setSelectedProviders(null);
    setSearchGames("");
    setSortOrder(false);
  };

  const toggleProviderSelection = (provider, key) => {
    setOffset(0);
    if (key === "clear") {
      setSelectedProviders(null);
      setOffset(0);
      setSearchGames("");
      setSortOrder(false);
    } else {
      setSelectedProviders(provider.provider_name);
      setOffset(0);
      setSearchGames("");
      setSortOrder(false);
    }
  };

  const fetchAllGamesDebounced = useCallback(
    debounce(async (value) => {
      if (!value || value.length < 3) return; // Don't run if search term is too short

      // Only perform the search if this is for pagination (pageNumber > 1) or it's a new search term
      if (pageNumber > 1 || value !== lastSearchRef.current) {
        lastSearchRef.current = value; // Update the last search term
        isLoadingRef.current = true;
        const params = {
          search: value.trim(),
          page: pageNumber,
          limit: limit,
        };
        try {
          setSearchLoading(true); // Make sure we're showing loading state
          const response = await gameService.fetchAllGamesByName(params);
          const newGames = response.data || [];

          if (pageNumber > 1) {
            setGames((prevGames) => [...prevGames, ...newGames]);
          } else {
            setGames(newGames);
          }

          setTotalGames(response.total || 0);
          setTotalPages(response.totalPages || 0);
          setHasMore(pageNumber < (response.totalPages || 0));
        } catch (error) {
          console.error("Failed to load games:", error);
          setGames([]);
          setTotalGames(0);
          setTotalPages(0);
          setHasMore(false);
        } finally {
          setSearchLoading(false);
          isLoadingRef.current = false; // Reset the loading guard
        }
      }
    }, 500),
    [pageNumber, limit, gameService]
  );

  const handleGameSearch = (value) => {
    setSearchGames(value);
    if (value.length >= 3) {
      // Critical fix: Force reset lastSearchRef to ensure the search API is called
      lastSearchRef.current = "";
      setSelectedCategory("");
      setSelectedProviders(null);
      setPageNumber(1);
      setIsSearchMode(true);
      setSearchLoading(true);

      // Direct invocation without condition to ensure the API is called
      const params = {
        search: value.trim(),
        page: 1,
        limit: limit,
      };

      setGames([]);

      // Call API directly first time to ensure it runs
      gameService
        .fetchAllGamesByName(params)
        .then((response) => {
          const newGames = response.data || [];
          setGames(newGames);
          setTotalGames(response.total || 0);
          setTotalPages(response.totalPages || 0);
          setHasMore(newGames.length > 0 && newGames.length < (response.total || 0));
          lastSearchRef.current = value; // Update after search complete
        })
        .catch((error) => {
          console.error("Failed to load games:", error);
          setGames([]);
          setTotalGames(0);
          setTotalPages(0);
          setHasMore(false);
        })
        .finally(() => {
          setSearchLoading(false);
          isLoadingRef.current = false;
        });
    } else if (value.length === 0) {
      setGames([]);
      setPageNumber(1);
      setOffset(0);
      setHasMore(true);
      setIsSearchMode(false);
      setSearchLoading(false);
      loadFilteredGames();
    }
  };

  useEffect(() => {
    return () => {
      fetchAllGamesDebounced.cancel();
    };
  }, [fetchAllGamesDebounced, selectedProviders, selectedCategory]);

  const handleLoadMore = useCallback(() => {
    if (loading || searchLoading || isLoadingRef.current || !hasMore) {
      return;
    }

    isLoadingRef.current = true;

    if (isSearchMode && searchGames.length >= 3) {
      setPageNumber((prevPage) => prevPage + 1);
    } else {
      setOffset((prevOffset) => prevOffset + limit);
      loadFilteredGames();
    }
  }, [hasMore, isSearchMode, limit, loading, loadFilteredGames, searchGames, searchLoading]);

  useEffect(() => {
    if (searchGames.length >= 3) {
      // Reset game list and pagination, but don't trigger a search - the handleGameSearch function will do that
      setGames([]);
      setPageNumber(1);
      setOffset(0);
      // Don't call fetchAllGamesDebounced here, that will be handled by handleGameSearch
    } else if (!searchGames.length) {
      // Reset search state when clearing search
      lastSearchRef.current = "";
      setSearchLoading(false);
      loadFilteredGames();
    }
  }, [category, selectedProviders, sortOrder, location, searchGames]);

  useEffect(() => {
    setGames([]);
    setTotalGames(0);
    setTotalPages(0);
    setOffset(0);
    setHasMore(true);
    setSelectedCategory("");
    setSelectedProviders(null);
    setSearchGames("");
    setSortOrder(false);
    // Make sure both loading states are reset when location changes
    setLoading(false);
    setSearchLoading(false);
    setIsSearchMode(false);

    loadFilteredGames();
  }, [location]);

  useEffect(() => {
    if (isEmpty(allProviders)) {
      setIsProviderLoading(true);
      const getAllProviders = async () => {
        try {
          const response = await gameService.fetchGamesProvider();
          setAllProviders(response.data);
        } catch (error) {
          console.error("Error fetching providers:", error);
          setError(error);
        } finally {
          setIsProviderLoading(false);
        }
      };
      getAllProviders();
    }
  }, []);

  useEffect(() => {
    if (isSearchMode && searchGames.length >= 3 && pageNumber > 1) {
      fetchAllGamesDebounced(searchGames);
    }
  }, [pageNumber, isSearchMode, searchGames, fetchAllGamesDebounced]);

  const filteredGames = games.filter((game) => game.name.toLowerCase().includes(searchGames.toLowerCase()));
  const displayedGames = filteredGames;

  const displayedProviders =
    allProviders && allProviders.length ? allProviders.filter((fi) => fi.provider_name.toLowerCase().includes(searchProviders?.toLowerCase())) : [];

  return (
    <div className="lg:sticky top-[100px]">
      {!isLiveCasino && (
        <GamesCategoriesSlider
          setSearchProviders={setSearchProviders}
          selectedProviders={selectedProviders}
          selectedCategory={selectedCategory}
          setSelectedCategory={handleSelectedType}
        />
      )}
      <div className="lg:sticky lg:top-[100px] z-10 bg-body mt-5">
        <GamesFiltersBar
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          searchGames={searchGames}
          handleGameSearch={handleGameSearch}
          searchProviders={searchProviders}
          setSearchProviders={setSearchProviders}
          selectedProviders={selectedProviders}
          toggleProviderSelection={toggleProviderSelection}
          isProvidersVisible={isProvidersVisible}
          setIsProvidersVisible={setIsProvidersVisible}
          isProvidersModal={isProvidersModal}
          setIsProvidersModal={setIsProvidersModal}
        />
      </div>
      <div className={cn("flex px-2 my-3 gap-3 w-full items-start")}>
        {!matchesMedia.isMobile ? (
          <div className={cn("sticky top-[160px] flex-1 w-full min-w-[235px] max-w-[235px]", isProvidersVisible ? "block" : "hidden")}>
            <ProviderListSearch
              data={displayedProviders}
              isMobile={matchesMedia.isMobile}
              selectedProviders={selectedProviders}
              toggleProviderSelection={toggleProviderSelection}
              isProvidersVisible={isProvidersVisible}
              setIsProvidersVisible={setIsProvidersVisible}
              searchProviders={searchProviders}
              setSearchProviders={setSearchProviders}
              isLoading={isProviderLoading}
            />
          </div>
        ) : null}

        <div className={cn("w-full hide-scrollbar")}>
          {!error ? (
            <>
              {/* Initial loading state - only show on first load */}
              {(loading || searchLoading) && !displayedGames.length ? (
                <div className="grid xl:grid-cols-7 lg:grid-col-4 md:grid-cols-3 grid-cols-2 gap-3">
                  {Array.from({ length: limit }).map((_, index) => (
                    <SkeletonLoader className="aspect-[5/3.5]" key={index} />
                  ))}
                </div>
              ) : displayedGames && displayedGames.length ? (
                <InfiniteScroll
                  pageStart={0}
                  loadMore={handleLoadMore}
                  hasMore={hasMore}
                  initialLoad={false}
                  threshold={500}
                  useWindow={true}
                  getScrollParent={() => window}
                  loader={
                    <div className="grid xl:grid-cols-7 lg:grid-col-4 md:grid-cols-3 grid-cols-2 gap-3 mt-3 mb-3" key={0}>
                      {Array.from({ length: Math.min(limit, 28) }).map((_, index) => (
                        <SkeletonLoader className="aspect-[5/3.5]" key={index} />
                      ))}
                    </div>
                  }
                >
                  <div className={`${"grid xl:grid-cols-7 lg:grid-col-4 md:grid-cols-3 grid-cols-2 gap-3 mb-3"}`}>
                    {displayedGames?.map((game, index) => (
                      <GameCard data={game} searchGamesCard key={game.id || index} />
                    ))}
                  </div>
                </InfiniteScroll>
              ) : (
                <div className="flex items-center justify-center w-full py-10 bg-body text-white h-[calc(100vh-200px)]">
                  {searchGames && searchGames.length >= 3 ? t("No games found for your search") : t("No games available at the moment")}...
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center w-full py-10 bg-body text-red-500">{error}</div>
          )}
        </div>
      </div>
      {isProvidersModal && (
        <Drawer
          title={
            <span
              className="text-white text-[16px] uppercase"
              onClick={() => (isEmpty(selectedProviders) ? setIsProvidersModal(false) : setIsProvidersModalWarning(true))}
            >
              <i className="bc-i-small-arrow-left text-[12px] mr-1" /> {t("back")}
            </span>
          }
          isOpen={isProvidersModal}
          onClose={() => setIsProvidersModal(false)}
        >
          <ProviderListSearch
            data={displayedProviders}
            isMobile={matchesMedia.isMobile}
            selectedProviders={selectedProviders}
            toggleProviderSelection={toggleProviderSelection}
            setIsProvidersVisible={setIsProvidersVisible}
            searchProviders={searchProviders}
            setSearchProviders={setSearchProviders}
            isProvidersVisible={true}
            isProvidersModal={isProvidersModal}
          />
          <div className="sm:hidden block w-full pt-2 pb-6 bg-body absolute bototm-0">
            <button
              className="rounded w-[calc(100%-20px)] mx-auto py-[10px] bg-white text-body"
              onClick={() => {
                setIsProvidersModal(false);
              }}
            >
              {t("Apply")}
            </button>
          </div>
        </Drawer>
      )}
      {isProvidersModalWarning ? (
        <Modal
          className="rounded w-[90%]"
          modalClassName="items-center"
          closeBtnClassName="mt-3 -mr-2"
          isOpen={isProvidersModalWarning}
          onClose={() => setIsProvidersModalWarning(false)}
        >
          <div className="text-center p-4">
            <p className="uppercase">{t("warning")}</p>
            <small>{t("You have unsaved changes")}</small>
            <Button
              size="small"
              label={t("Save and Exit")}
              className="text-sm mt-4 w-full py-2 bg-green-700 uppercase"
              onClick={() => {
                setIsProvidersModalWarning(false);
                setIsProvidersModal(false);
              }}
            />
            <Button
              size="small"
              variant="outline"
              label={t("Close Anyway")}
              className="text-sm mt-3 w-full py-2 uppercase border border-white/40"
              onClick={() => {
                toggleProviderSelection(null, "clear");
                setIsProvidersModalWarning(false);
                setIsProvidersModal(false);
              }}
            />
          </div>
        </Modal>
      ) : null}
    </div>
  );
};
